/* THE QUEUE SOMEBODY ACTUALLY READS.

   Jay Jung, 2026-10-07: "guest register by themselves first and then admin review/edit/approve."
   The form is half of that; without this screen the requests pile up where nobody can see them,
   which is worse than not having the form at all — a partner would be waiting on a decision
   nobody knows they are supposed to make.

   APPROVING DOES NOT CREATE THE ACCOUNT HERE. It hands the details to Register, where the ROLES
   are chosen, because that is the one decision a stranger's form cannot make and it is not one to
   take by pressing a button on a list. The request is marked approved when Register comes back
   with a username, so a row never says "approved" for an account that was never made.

   REJECTING ASKS WHY and the server asks again, since a reason typed into a form that is never
   sent anywhere is a reason nobody can read. */
import "./style.css";
import "./SPHub.css";
import "./ProductLine.css";
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Check, X, RefreshCw, UserPlus, ShieldCheck, Building2, Users as UsersIcon } from "lucide-react";
import { useLocation } from "react-router-dom";
import { RoleRequestsTab, PartnerAccountsTab, TeamsRolesTab } from "./SignupAdmin";
import Header, { forgetAccessBadge } from "./Header";
import { SortTh, sortRows, rowPassesColumnFilters, distinctColumnValues } from "./15_SampleTracker";
import { BACKEND_URL } from "./backendConfig";
import { useTranslation, Trans } from "react-i18next";
import i18n from "./i18n";
import ModuleHelp from "./ModuleHelp";

/* THE GUIDE (Max 01/11: every module has one). With nobody waiting there are no rows to act on, so the row-buttons step is
   skipped by TourGuide. */
const TOUR = [
  { selector: '.sp-note', title: 'tourAccess.noteT', body: 'tourAccess.noteB' },
  { selector: '.pl-page .pl-rule-edit', title: 'tourAccess.toolsT', body: 'tourAccess.toolsB' },
  { selector: '[data-tour="ar-waiting"]', title: 'tourAccess.waitingT', body: 'tourAccess.waitingB' },
  { selector: '[data-tour="ar-waiting"] tbody .pl-rule-edit', title: 'tourAccess.actionT', body: 'tourAccess.actionB' },
  { selector: '[data-tour="ar-decided"]', title: 'tourAccess.decidedT', body: 'tourAccess.decidedB' },
];

const API = `${BACKEND_URL}/api/access-requests`;

const when = (v) => (v ? String(v).replace('T', ' ').slice(0, 16) : '');

/* The stored status ('pending' / 'approved' / 'rejected') is what the code compares; the cell and the funnel
   print its label in the reader's language (read at render, so a language switch redraws it). */
const statusLabel = (st) => (['approved', 'rejected', 'pending'].includes(st) ? i18n.t(`xAccess.status.${st}`) : (st || ''));

/* WHAT EACH COLUMN HOLDS, for sorting and for the funnel — Max, 2026-10-07: "cái bảng ở Waiting
   và Decided thì đều cho sort và filter như excel nha".

   ONE MAP FOR BOTH TABLES. They share Asked / Name / Email / Company and differ after that; two
   maps would be two places to forget a column, and the two tables are the same rows at two
   stages. The funnel lists what a column ACTUALLY holds, so a getter must return the string the
   cell prints — a date formatted the way the cell formats it, and the em dash the cell shows for
   an empty one, or the funnel would offer a value nobody can see in the table. */
const AR_GETTERS = {
    createdAt: (r) => when(r.createdAt),
    fullName: (r) => r.fullName || '',
    email: (r) => r.email || '',
    company: (r) => r.company || '',
    contact: (r) => r.contact || '—',
    reason: (r) => r.reason || '—',
    status: (r) => statusLabel(r.status),
    decidedBy: (r) => r.decidedBy || '—',
    decidedAt: (r) => when(r.decidedAt),
    note: (r) => r.note || '—',
    username: (r) => r.username || '—',
};

// The key of each column's heading in xAccess.col — translated where it is drawn.
const AR_LABEL = {
    createdAt: 'asked', fullName: 'name', email: 'email', company: 'company',
    contact: 'contact', reason: 'reason', status: 'status',
    decidedBy: 'decidedBy', decidedAt: 'decidedAt', note: 'note', username: 'username',
};

/* THE TABS (Jay tasks 02/11 #1160 / #1155): account requests (the form, as before), the role a new person's team asks for,
   partner accounts waiting to sign in, and the team → role table. ?tab= opens one (the bell's links do). */
const AR_TABS = ['accounts', 'roles', 'partners', 'teams'];

function AccessRequests() {
    const { t } = useTranslation();
    const colLabel = (k) => (AR_LABEL[k] ? t(`xAccess.col.${AR_LABEL[k]}`) : k);
    const navigate = useNavigate();
    const [rows, setRows] = useState(null);
    const location = useLocation();
    const [tab, setTabState] = useState(() => { const v = new URLSearchParams(location.search).get('tab'); return AR_TABS.includes(v) ? v : 'accounts'; });
    const setTab = (k) => { setTabState(k); try { window.history.replaceState(null, '', `${window.location.pathname}${k === 'accounts' ? '' : `?tab=${k}`}`); } catch (e) { /* ignore */ } };
    const [counts, setCounts] = useState({ roles: 0, partners: 0 });
    const loadCounts = useCallback(() => fetch(`${BACKEND_URL}/api/admin/signup-counts`, { credentials: 'include' })
        .then((r) => (r.ok ? r.json() : null)).then((d) => { if (d) setCounts(d); }).catch(() => {}), []);
    useEffect(() => { loadCounts(); }, [loadCounts]);
    const [msg, setMsg] = useState(null);
    const [busy, setBusy] = useState(false);
    const [rejecting, setRejecting] = useState(null);   // the id whose reason box is open
    const [note, setNote] = useState('');

    /* A SORT AND A SET OF FUNNELS PER TABLE, never shared. Waiting and Decided are two different
       populations: a funnel set on one must not silently narrow the other, which is what one
       shared state would do the moment somebody filtered by company. */
    const [sortW, setSortW] = useState({ key: '', dir: 'asc' });
    const [colsW, setColsW] = useState({});
    const [openW, setOpenW] = useState(null);
    const [sortD, setSortD] = useState({ key: '', dir: 'asc' });
    const [colsD, setColsD] = useState({});
    const [openD, setOpenD] = useState(null);

    const load = useCallback(async () => {
        try {
            const r = await fetch(API, { credentials: 'include' });
            const b = await r.json();
            setRows(b.rows || []);
        } catch (e) {
            setMsg({ bad: true, text: t('xAccess.loadFailed') });
            setRows([]);
        }
    // `t` is left out on purpose: a language switch must not read the requests again.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => { load(); }, [load]);

    const decide = async (id, decision, body = {}) => {
        setBusy(true);
        try {
            const r = await fetch(`${API}/${id}/decide`, {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ decision, ...body }),
            });
            const b = await r.json();
            if (!r.ok) {
                setMsg({ bad: true, text: b.error || t('xAccess.failed') });
            } else {
                forgetAccessBadge();
                /* WHAT HAPPENED AND WHAT IT DID, not the value that was stored — Max,
                   2026-10-07: "chỗ Marked rejected. là gì". "Marked rejected." named the column
                   it had just written, which is the portal talking about itself; the thing the
                   person pressing it wants to know is that the request is closed AND that the
                   applicant was told, because that is the half that leaves the building. */
                setMsg({
                    bad: false,
                    text: decision === 'rejected'
                        ? t('xAccess.rejectedMsg')
                        : t('xAccess.recorded'),
                });
                setRejecting(null);
                setNote('');
                await load();
            }
        } finally {
            setBusy(false);
        }
    };

    /* Register reads these off the query string. The request id travels with them so the screen
       that comes back knows which row to mark — see 01_Register.jsx. */
    const openInRegister = async (r) => {
        /* The username is SUGGESTED by the server, which already knows which names are free —
           see /api/access-requests/:id/username. Asked for here rather than guessed in the
           browser: a second implementation of "is this taken" is how two callers come to
           disagree about it. A failure just leaves the box empty, which is where it was before. */
        let suggestion = '';
        try {
            const res = await fetch(`${API}/${r.id}/username`, { credentials: 'include' });
            suggestion = (await res.json()).suggestion || '';
        } catch (e) { /* the admin types one */ }
        /* THE HALVES AS THEY WERE TYPED. This used to split fullName on a space, which is the
           wrong guess for a Vietnamese or Korean name — "Bui Chi Loi" was filed with the FAMILY
           name as the first name. The form asks for both since 2026-10-07.
           The split survives for rows raised BEFORE that, where the columns are null: those were
           never asked, and a guess the admin can see and correct in Register beats an empty
           form. */
        const split = (r.fullName || '').trim().split(/\s+/);
        const q = new URLSearchParams({
            request: String(r.id),
            email: r.email || '',
            username: suggestion,
            firstName: r.firstName || split[0] || '',
            lastName: r.lastName || split.slice(1).join(' '),
        });
        navigate(`/register?${q.toString()}`);
    };

    /* ONE header-prop builder for both tables, because they behave the same — the same reason
       CEO Office keeps one: two copies is how a funnel comes to clear on one table and stick on
       the other. */
    const th = (key, sort, setSort, cols, setCols, open, setOpen, rowsBefore) => ({
        sortKey: key,
        sort,
        /* THE HEADER TOGGLES, THE MENU SETS. ColumnFilterMenu's own "Sort ascending" passes the
           direction it means; ignoring it — which every other caller of SortTh in this codebase
           does — makes a button labelled A→Z flip to Z→A when the column is already A→Z. A click
           on the label still has no direction to give, so that one toggles. */
        onSort: (k, dir) => setSort((p) => (dir
            ? { key: k, dir }
            : p.key === k
                ? { key: k, dir: p.dir === 'asc' ? 'desc' : 'asc' }
                : { key: k, dir: 'asc' })),
        filterValues: open?.key === key
            ? distinctColumnValues(rowsBefore, key, cols, AR_GETTERS) : undefined,
        filterSelected: cols[key],
        filterOpen: open?.key === key,
        filterAnchor: open?.key === key ? open.anchor : null,
        onOpenFilter: (k, anchor) => setOpen((prev) => (prev && prev.key === k ? null : { key: k, anchor })),
        onCloseFilter: () => setOpen(null),
        onApplyFilter: (k, values, allValues) => {
            setCols((prev) => {
                const next = { ...prev };
                // Everything ticked is the same as no filter — keeping it lights the funnel for ever.
                const isAll = allValues && allValues.length && values.length >= allValues.length;
                if (!values.length || isAll) delete next[k]; else next[k] = values;
                return next;
            });
            setOpen(null);
        },
    });

    const rawPending = (rows || []).filter((r) => r.status === 'pending');
    const rawDecided = (rows || []).filter((r) => r.status !== 'pending');

    const narrow = (cols, sort) => [
        ...Object.entries(cols).map(([k, v]) => t('xAccess.filterBit', { col: colLabel(k), values: v.join(', ') })),
        sort.key ? t('xAccess.sortedBy', { col: colLabel(sort.key) }) : null,
    ].filter(Boolean);

    const cut = (all, cols, sort) => {
        const afterCols = Object.keys(cols).length
            ? all.filter((r) => rowPassesColumnFilters(r, cols, AR_GETTERS)) : all;
        return sort.key ? sortRows(afterCols, sort, AR_GETTERS) : afterCols;
    };
    const pending = cut(rawPending, colsW, sortW);
    const decided = cut(rawDecided, colsD, sortD);

    const wTh = (key) => th(key, sortW, setSortW, colsW, setColsW, openW, setOpenW, rawPending);
    const dTh = (key) => th(key, sortD, setSortD, colsD, setColsD, openD, setOpenD, rawDecided);

    /* WHAT IS NARROWING THE TABLE, AND THE ONE WAY OUT. A column funnel is a filter nobody can
       see from anywhere but the column it sits on, so a table somebody narrowed yesterday
       obeys the portal's own rule: a list narrowed by something nothing on screen mentions is
       the same as a broken list. */
    const narrowLine = (cols, sort, clear, total, shown) => {
        const bits = narrow(cols, sort);
        if (!bits.length) return null;
        return (
            <p className="sp-sub ar-narrow">
                {t('xAccess.showing', { shown, total, bits: bits.join(' · ') })}
                <button type="button" className="sp-btn" onClick={clear}>{t('xAccess.clear')}</button>
            </p>
        );
    };

    return (
        /* THE SAME SHELL EVERY OTHER SCREEN USES — Max, 2026-10-07, on a screenshot where the
           page title was hidden behind the top bar and the first column sat under the sidebar.
           Header OUTSIDE, then .content, then .sp-page: .content is what leaves room for the
           sidebar and the fixed bar, and this screen was missing it. sp-wrap and sp-head were
           invented here and exist in no stylesheet, so they styled nothing at all. */
        <div>
            <Header />
            <div className="content">
            <div className="sp-page pl-page">
                <div className="mh-titlerow">
                    <div className="sp-title">
                        <h2>{t('xAccess.title')}</h2>
                    </div>
                    <ModuleHelp steps={TOUR} tourKey="accessRequests" ready={rows !== null} onBeforeTour={() => { setTab('accounts'); setRejecting(null); setNote(''); }} />
                </div>
                <div className="view-tabs" role="tablist" aria-label={t('xAccess.title')}>
                    {[['accounts', UserPlus, t('xSignup.tabAccounts'), (rows || []).filter((r) => r.status === 'pending').length],
                      ['roles', ShieldCheck, t('xSignup.tabRoles'), counts.roles], ['partners', Building2, t('xSignup.tabPartners'), counts.partners],
                      ['teams', UsersIcon, t('xSignup.tabTeams'), 0]].map(([k, Icon, label, n]) => (
                        <button key={k} type="button" role="tab" aria-selected={tab === k} className={tab === k ? 'on' : ''} onClick={() => setTab(k)} data-ar-tab={k}>
                            <Icon size={16} aria-hidden="true" /> {label}{n > 0 && <span className="pa-count alert">{n}</span>}
                        </button>
                    ))}
                </div>
                {tab === 'roles' && <RoleRequestsTab onChanged={loadCounts} />}
                {tab === 'partners' && <PartnerAccountsTab onChanged={loadCounts} />}
                {tab === 'teams' && <TeamsRolesTab />}
                {tab === 'accounts' && (<>
                <div className="sp-section-head" style={{ border: 0, paddingLeft: 0 }}>
                    <div />
                    <div className="pl-rule-edit">
                        <button type="button" className="sp-btn" onClick={load}>
                            <RefreshCw size={13} /> {t('xAccess.refresh')}
                        </button>
                        <button type="button" className="sp-btn" onClick={() => navigate('/list-user')}>
                            <ArrowLeft size={13} /> {t('xAccess.users')}
                        </button>
                    </div>
                </div>
                <p className="sp-note">
                    <Trans t={t} i18nKey="xAccess.note" components={{ b: <b /> }} />
                </p>
                {msg && (
                    <p className={msg.bad ? 'sp-error' : 'sp-ok'}>{msg.text}</p>
                )}

                <section className="sp-section" data-tour="ar-waiting">
                    <div className="sp-section-head">
                        <h3>{pending.length !== rawPending.length
                            ? t('xAccess.waitingOf', { n: pending.length, total: rawPending.length })
                            : t('xAccess.waiting', { n: pending.length })}</h3>
                    </div>
                    {narrowLine(colsW, sortW, () => { setColsW({}); setSortW({ key: '', dir: 'asc' }); },
                                rawPending.length, pending.length)}
                    {rows === null ? <p className="sp-sub">{t('xAccess.loading')}</p> : !pending.length ? (
                        <p className="sp-sub">
                            {rawPending.length ? t('xAccess.noMatch') : t('xAccess.nothingWaiting')}
                        </p>
                    ) : (
                        <div className="pl-scroll"><table className="sp-table pl-table">
                            <thead>
                                <tr>
                                    <SortTh label={colLabel('createdAt')} {...wTh('createdAt')} />
                                    <SortTh label={colLabel('fullName')} {...wTh('fullName')} />
                                    <SortTh label={colLabel('email')} {...wTh('email')} />
                                    <SortTh label={colLabel('company')} {...wTh('company')} />
                                    <SortTh label={colLabel('contact')} {...wTh('contact')} />
                                    <SortTh label={colLabel('reason')} {...wTh('reason')} />
                                    {/* Action holds buttons, not a value: there is nothing to
                                        sort it by and nothing to list in a funnel. */}
                                    <th>{t('xAccess.col.action')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {pending.map((r) => (
                                    <tr key={r.id}>
                                        <td>{when(r.createdAt)}</td>
                                        <td>{r.fullName}</td>
                                        <td>{r.email}</td>
                                        <td>{r.company}</td>
                                        <td>{r.contact || '—'}</td>
                                        <td className="ar-reason">{r.reason || '—'}</td>
                                        <td>
                                            {rejecting === r.id ? (
                                                <span className="pl-rule-edit ar-why-row">
                                                    <input
                                                        className="sp-input ar-why"
                                                        placeholder={t('xAccess.whyPh')}
                                                        value={note}
                                                        onChange={(e) => setNote(e.target.value)}
                                                    />
                                                    <button type="button" className="sp-btn"
                                                        disabled={busy || !note.trim()}
                                                        onClick={() => decide(r.id, 'rejected', { note })}>
                                                        {t('xAccess.reject')}
                                                    </button>
                                                    <button type="button" className="sp-btn"
                                                        onClick={() => { setRejecting(null); setNote(''); }}>
                                                        {t('xAccess.cancel')}
                                                    </button>
                                                </span>
                                            ) : (
                                                <span className="pl-rule-edit">
                                                    <button type="button" className="sp-btn primary"
                                                        onClick={() => openInRegister(r)}>
                                                        <Check size={12} /> {t('xAccess.createAccount')}
                                                    </button>
                                                    <button type="button" className="sp-btn"
                                                        onClick={() => { setRejecting(r.id); setNote(''); }}>
                                                        <X size={12} /> {t('xAccess.reject')}
                                                    </button>
                                                </span>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table></div>
                    )}
                </section>

                {/* A REJECTED ROW IS KEPT and shown. "Rejected on the 9th because they are not a
                    vendor" explains a second request from the same address; a vanished row
                    explains nothing and invites the same question again. */}
                <section className="sp-section" data-tour="ar-decided">
                    <div className="sp-section-head">
                        <h3>{decided.length !== rawDecided.length
                            ? t('xAccess.decidedOf', { n: decided.length, total: rawDecided.length })
                            : t('xAccess.decided', { n: decided.length })}</h3>
                    </div>
                    {narrowLine(colsD, sortD, () => { setColsD({}); setSortD({ key: '', dir: 'asc' }); },
                                rawDecided.length, decided.length)}
                    {!decided.length ? (
                        <p className="sp-sub">
                            {rawDecided.length ? t('xAccess.noMatch') : t('xAccess.nothingDecided')}
                        </p>
                    ) : (
                        <div className="pl-scroll"><table className="sp-table pl-table">
                            <thead>
                                <tr>
                                    <SortTh label={colLabel('createdAt')} {...dTh('createdAt')} />
                                    <SortTh label={colLabel('fullName')} {...dTh('fullName')} />
                                    <SortTh label={colLabel('email')} {...dTh('email')} />
                                    <SortTh label={colLabel('company')} {...dTh('company')} />
                                    <SortTh label={colLabel('status')} {...dTh('status')} />
                                    <SortTh label={colLabel('decidedBy')} {...dTh('decidedBy')} />
                                    <SortTh label={colLabel('decidedAt')} {...dTh('decidedAt')} />
                                    <SortTh label={colLabel('note')} {...dTh('note')} />
                                    <SortTh label={colLabel('username')} {...dTh('username')} />
                                </tr>
                            </thead>
                            <tbody>
                                {decided.map((r) => (
                                    <tr key={r.id}>
                                        <td>{when(r.createdAt)}</td>
                                        <td>{r.fullName}</td>
                                        <td>{r.email}</td>
                                        <td>{r.company}</td>
                                        <td>{statusLabel(r.status)}</td>
                                        <td>{r.decidedBy || '—'}</td>
                                        <td>{when(r.decidedAt)}</td>
                                        <td className="ar-reason">{r.note || '—'}</td>
                                        <td>{r.username || '—'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table></div>
                    )}
                </section>
                </>)}
            </div>
            </div>
        </div>
    );
}

export default AccessRequests;
